import { CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import "./App.css";
import { AuthProvider } from "./contexts/AuthContext";
import {
  ThemeProvider as CustomThemeProvider,
  useTheme,
} from "./contexts/ThemeContext";

// Public routes
import HomePage from "./pages/HomePage";
import NotFoundPage from "./pages/NotFoundPage";

// Admin routes
import AdminLayout from "./components/admin/AdminLayout";
import ProtectedRoute from "./components/routing/ProtectedRoute";
import CertificationsPage from "./pages/admin/CertificationsPage";
import CreateCertificationPage from "./pages/admin/CreateCertificationPage";
import CreateProjectPage from "./pages/admin/CreateProjectPage";
import CreateTechnologyPage from "./pages/admin/CreateTechnologyPage";
import DashboardPage from "./pages/admin/DashboardPage";
import EditCertificationPage from "./pages/admin/EditCertificationPage";
import EditProjectPage from "./pages/admin/EditProjectPage";
import EditTechnologyPage from "./pages/admin/EditTechnologyPage";
import LoginPage from "./pages/admin/LoginPage";
import ProjectsPage from "./pages/admin/ProjectsPage";
import RegisterPage from "./pages/admin/RegisterPage";
import TechnologiesPage from "./pages/admin/TechnologiesPage";
import ConfigurationsPage from "./pages/admin/ConfigurationsPage";
import UsersPage from "./pages/admin/UsersPage";

// Theme configurations
const getTheme = (mode) => {
  return createTheme({
    palette: {
      mode: mode,
      primary: {
        main: "#f50057", // Bright pink
      },
      secondary: {
        main: "#8e24aa", // Purple color
      },
      ...(mode === "dark"
        ? {
            // Dark mode colors
            background: {
              default: "#0a0a1a", // Very dark blue/black
              paper: "#121229", // Slightly lighter dark blue
            },
            text: {
              primary: "#ffffff",
              secondary: "rgba(255, 255, 255, 0.7)",
            },
          }
        : {
            // Light mode colors
            background: {
              default: "#f9f9fb", // Light grayish white
              paper: "#ffffff", // White
            },
            text: {
              primary: "#1a1a2e",
              secondary: "rgba(0, 0, 0, 0.7)",
            },
          }),
    },
    typography: {
      fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
      h1: {
        fontWeight: 700,
      },
      h2: {
        fontWeight: 700,
      },
      h3: {
        fontWeight: 600,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: "none",
            fontWeight: 600,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          },
        },
      },
    },
  });
};

const AppContent = () => {
  const { mode } = useTheme();
  const theme = getTheme(mode);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />

            {/* Admin routes */}
            <Route path="/admin/login" element={<LoginPage />} />
            <Route path="/admin/register" element={<RegisterPage />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/admin" element={<AdminLayout />}>
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="projects" element={<ProjectsPage />} />
                <Route path="projects/new" element={<CreateProjectPage />} />
                <Route path="projects/edit/:id" element={<EditProjectPage />} />
                <Route path="technologies" element={<TechnologiesPage />} />
                <Route
                  path="technologies/new"
                  element={<CreateTechnologyPage />}
                />
                <Route
                  path="technologies/edit/:id"
                  element={<EditTechnologyPage />}
                />
                <Route path="certifications" element={<CertificationsPage />} />
                <Route
                  path="certifications/new"
                  element={<CreateCertificationPage />}
                />
                <Route
                  path="certifications/edit/:id"
                  element={<EditCertificationPage />}
                />
                <Route path="configurations" element={<ConfigurationsPage />} />
                <Route path="users" element={<UsersPage />} />
              </Route>
            </Route>

            {/* 404 Page */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

function App() {
  return (
    <CustomThemeProvider>
      <AppContent />
    </CustomThemeProvider>
  );
}

export default App;
